import moment from 'moment';
import {
  SeminarConditionType,
  SeminarCreateUpdateModel,
  SeminarDateSeriesDetailsModel,
  SeminarDateSeriesListModel,
  SeminarFilterOptionModel,
  SeminarStatus,
} from '../models/seminar.model';
import { getUserFullName } from './user.utils';

export function getSeminarLocationString(
  seminarDateSeries: SeminarDateSeriesListModel,
  useLineBreaks: boolean = false,
  shortForm: boolean = false
): string {
  if (seminarDateSeries.seminar.modality === 'online') {
    return 'Online';
  }
  let separator = useLineBreaks ? '\n' : ', ';
  let tooltip = '';

  if (!seminarDateSeries.seminar.address) {
    return '';
  }

  if (seminarDateSeries.seminar.address.title && !shortForm) {
    tooltip += seminarDateSeries.seminar.address.title + separator;
  }
  if (seminarDateSeries.seminar.address.street) {
    tooltip += seminarDateSeries.seminar.address.street + ' ';
  }
  if (seminarDateSeries.seminar.address.houseNumber) {
    tooltip += seminarDateSeries.seminar.address.houseNumber + separator;
  }
  if (seminarDateSeries.seminar.address.zip) {
    tooltip += seminarDateSeries.seminar.address.zip + ' ';
  }
  if (seminarDateSeries.seminar.address.city) {
    tooltip += seminarDateSeries.seminar.address.city + separator;
  }
  if (seminarDateSeries.seminar.address.country && !shortForm) {
    tooltip += seminarDateSeries.seminar.address.country;
  }
  return tooltip.trim().replace(/,\s*$/, '');
}

export function getSeminarDateString(
  seminarDateSeries: SeminarDateSeriesListModel
): string {
  if (!seminarDateSeries.dates || seminarDateSeries.dates.length === 0) {
    return null;
  }
  if (seminarDateSeries.dates.length === 1) {
    return seminarDateSeries.dates[0].startDate
      ? moment(seminarDateSeries.dates[0].startDate).format('DD.MM.YYYY')
      : null;
  }

  // Parse and sort dates to ensure they're in chronological order
  const sortedDates = seminarDateSeries.dates.sort((a, b) => {
    return moment(a.startDate).diff(moment(b.startDate));
  });

  // Check if dates are consecutive
  let consecutive = true;
  for (let i = 1; i < sortedDates.length; i++) {
    const date1 = moment(sortedDates[i - 1].startDate);
    const date2 = moment(sortedDates[i].startDate);

    const hourDiff = date2.diff(date1, 'hours');

    // Check if dates are within 0 and 48 hours apart
    if (hourDiff <= 0 || hourDiff > 48) {
      consecutive = false;
      break;
    }
  }

  if (consecutive) {
    // If dates are consecutive, return as a range
    const startDate = moment(sortedDates[0].startDate).format('DD.MM.');
    const endDate = moment(
      sortedDates[sortedDates.length - 1].startDate
    ).format('DD.MM.YYYY');
    return `${startDate} - ${endDate}`;
  } else {
    // If not, return joined by commas
    return sortedDates
      .map(date => moment(date.startDate).format('DD.MM.YYYY'))
      .join(', ');
  }
}

export function getSeminarDates(
  seminarDateSeries: SeminarDateSeriesListModel
): Date[] {
  if (!seminarDateSeries.dates || seminarDateSeries.dates.length === 0) {
    return [];
  }
  return seminarDateSeries.dates.map(date => date.startDate);
}

export function getSeminarFobiPoints(
  seminarDateSeries: SeminarDateSeriesListModel | SeminarDateSeriesDetailsModel
): number {
  if (!seminarDateSeries?.seminar?.fobiPoints) {
    return null;
  }
  return seminarDateSeries.dates?.reduce(
    (acc, date) => acc + date.fobiPoints,
    0
  );
}

export function getSeminarUE(
  seminarDateSeries: SeminarDateSeriesListModel | SeminarDateSeriesDetailsModel
) {
  return seminarDateSeries.dates?.reduce(
    (acc, date) => acc + date.fobiPoints,
    0
  );
}

export function getSeminarOccupancyColor(
  seminarDateSeries: SeminarDateSeriesListModel | SeminarDateSeriesDetailsModel
): string {
  if (
    seminarDateSeries.dates?.find(item =>
      moment(item.startDate).isBefore(moment())
    ) &&
    seminarDateSeries.dates?.find(item =>
      moment(item.endDate).isAfter(moment())
    )
  ) {
    return 'green';
  }
  if (seminarDateSeries.seminar.isCanceled) {
    return 'red';
  }
  const freeSeats =
    seminarDateSeries.seminar.maxParticipants -
    seminarDateSeries.participantCount;

  if (freeSeats <= 0) {
    return 'red';
  } else if (freeSeats < 5) {
    return 'yellow';
  } else {
    return 'green';
  }
}

export function getSeminarStatus(
  seminarDateSeries: SeminarDateSeriesListModel
): SeminarStatus {
  if (seminarDateSeries.seminar.isDraft) {
    return SeminarStatus.DRAFT;
  }

  if (seminarDateSeries.seminar.isCanceled) {
    return SeminarStatus.CANCELED;
  }

  if (!seminarDateSeries.dates || seminarDateSeries.dates.length === 0) {
    return SeminarStatus.UNKNOWN;
  }
  const allDatesCancelled = seminarDateSeries.dates
    .map(date => date)
    .every(date => date.isCanceled);
  if (allDatesCancelled) {
    return SeminarStatus.CANCELED;
  }

  // check if all course dates are in the past
  const allDatesInPast = seminarDateSeries.dates
    .map(date => date)
    .every(date => moment(date.startDate).isBefore(moment()));
  if (allDatesInPast) {
    return SeminarStatus.FINISHED;
  }

  if (
    seminarDateSeries.seminar.maxParticipants ===
    seminarDateSeries.participantCount
  ) {
    return SeminarStatus.WAITLIST;
  }

  // check if one date is in the past and at least one date is in the future
  const oneDateInPast = seminarDateSeries.dates
    .map(date => date)
    .some(
      date => moment(date.startDate).isBefore(moment()) && !date.isCanceled
    );
  const oneDateInFuture = seminarDateSeries.dates
    .map(date => date)
    .some(date => moment(date.startDate).isAfter(moment()) && !date.isCanceled);
  if (oneDateInPast && oneDateInFuture) {
    return SeminarStatus.RUNNING;
  }

  // check if all course dates are in the future
  // const allDatesInFuture = seminarDateSeries.dates
  //   .map(date => date)
  //   .every(date => moment(date.startDate).isAfter(moment()));
  // if (allDatesInFuture) {
  //   return SeminarStatus.PLANNED;
  // }

  return SeminarStatus.ONLINE;
}

export function getSeminarStatusChipClass(
  seminarStatus: SeminarStatus
): string {
  switch (seminarStatus) {
    case SeminarStatus.CANCELED:
      return 'inwi-chip-text-red-outline';
    case SeminarStatus.WAITLIST:
      return 'inwi-chip-text-red-outline';
    case SeminarStatus.FINISHED:
      return 'inwi-chip-text-grey-outline';
    case SeminarStatus.DRAFT:
      return 'inwi-chip-text-grey-outline';
    case SeminarStatus.RUNNING:
      return 'inwi-chip-text-green-outline';
    case SeminarStatus.ONLINE:
      return 'inwi-chip-text-green-outline';
    case SeminarStatus.PLANNED:
      return 'inwi-chip-text-green-outline';
    default:
      return 'inwi-chip-text-grey-outline';
  }
}

export function getSeminarStatusChipText(seminarStatus: SeminarStatus): string {
  switch (seminarStatus) {
    case SeminarStatus.CANCELED:
      return 'Abgesagt';
    case SeminarStatus.WAITLIST:
      return 'Warteliste';
    case SeminarStatus.FINISHED:
      return 'Vorbei';
    case SeminarStatus.DRAFT:
      return 'Entwurf';
    case SeminarStatus.RUNNING:
      return 'Läuft';
    case SeminarStatus.ONLINE:
      return 'Online';
    case SeminarStatus.PLANNED:
      return 'Geplant';
    default:
      return 'Unbekannt';
  }
}

export function getSeminarLecturerText(
  seminarDateSeries: SeminarDateSeriesDetailsModel | SeminarDateSeriesListModel
) {
  if (seminarDateSeries.lecturer2) {
    return `${getUserFullName(seminarDateSeries.lecturer1)} & ${getUserFullName(seminarDateSeries.lecturer2)}`;
  }
  return `${getUserFullName(seminarDateSeries.lecturer1) || ''}`;
}

export function hasValidSeminarPrice(
  seminar: SeminarCreateUpdateModel
): boolean {
  return seminar.conditions.some(
    condition => condition.price !== null && condition.price !== undefined
  );
}

export function translateCondition(condition: SeminarConditionType): string {
  switch (condition) {
    case SeminarConditionType.REGULAR:
      return 'Regulärer Preis';
    case SeminarConditionType.EARLY:
      return 'Frühbucherpreis';
    case SeminarConditionType.BED:
      return 'BED Preis';
    case SeminarConditionType.OFFICE:
      return 'Praxis Preis';
    case SeminarConditionType.LOGO:
      return 'LOGO Deutschland Mitglied';
    case SeminarConditionType.VDB:
      return 'VDB Mitglied';
    case SeminarConditionType.VDP:
      return 'VDP Mitglied';
    default:
      return 'Unbekannt';
  }
}

export function getParticipantConditionLabel(condition: string): string {
  switch (condition) {
    case SeminarConditionType.VDB:
      return 'VDB Mitglied';
    case SeminarConditionType.VDP:
      return 'VDP Mitglied';
    case SeminarConditionType.EARLY:
      return 'Frühbucher';
    case SeminarConditionType.BED:
      return 'BED Mitglied';
    case SeminarConditionType.OFFICE:
      return 'Praxis (ab 3 Personen)';
    case SeminarConditionType.LOGO:
      return 'LOGO Deutschland Mitglied';
    case SeminarConditionType.REGULAR:
    default:
      return 'Regulär';
  }
}

export function getSeminarFirstDateString(
  seminar: SeminarDateSeriesListModel | SeminarFilterOptionModel
): string {
  if (!seminar.dates || seminar.dates.length === 0) {
    return '';
  }
  if (
    moment(seminar.dates[0].startDate).isSame(
      seminar.dates[seminar.dates.length - 1].endDate,
      'day'
    )
  ) {
    return (
      moment(seminar.dates[0].startDate).format('DD.MM.YYYY HH:mm') +
      ' - ' +
      moment(seminar.dates[0].endDate).format('HH:mm') +
      ' Uhr'
    );
  }
  if (
    moment(seminar.dates[0].startDate).isSame(
      seminar.dates[seminar.dates.length - 1].endDate,
      'year'
    )
  ) {
    return (
      moment(seminar.dates[0].startDate).format('DD.MM.') +
      ' - ' +
      moment(seminar.dates[seminar.dates.length - 1].endDate).format(
        'DD.MM.YYYY'
      ) +
      ' ' +
      moment(seminar.dates[0].startDate).format('HH:mm') +
      ' - ' +
      moment(seminar.dates[seminar.dates.length - 1].endDate).format('HH:mm') +
      ' Uhr'
    );
  }
  return (
    moment(seminar.dates[0].startDate).format('DD.MM.YYYY') +
    ' - ' +
    moment(seminar.dates[seminar.dates.length - 1].endDate).format(
      'DD.MM.YYYY'
    ) +
    ' ' +
    moment(seminar.dates[0].startDate).format('HH:mm') +
    ' - ' +
    moment(seminar.dates[seminar.dates.length - 1].endDate).format('HH:mm') +
    ' Uhr'
  );
}
